import React from 'react';
import ReactGA from 'react-ga';
import ImageLightbox from '../../../components/imageLightbox/ImageLightbox';
import { useImageLightbox } from '../../../components/imageLightbox/useImageLightbox';
import nbnbHeroLanding from '../../../assets/images/nbnbHeroLanding.gif';
import takeawaysEmoji from '../../../assets/images/conditionsCaseStudy/takeaways-emoji.svg';
import overviewStats from '../../../assets/images/nbnb/overview-stats.png';
import problemCompetitor from '../../../assets/images/nbnb/problem-competitor-analysis.png';
import researchThemes from '../../../assets/images/nbnb/research-interview-themes.png';
import researchInsight from '../../../assets/images/nbnb/research-key-insight.png';
import quoteAvatarP2 from '../../../assets/images/nbnb/quote-avatar-p2.png';
import quoteAvatarP3 from '../../../assets/images/nbnb/quote-avatar-p3.png';
import quoteAvatarP7 from '../../../assets/images/nbnb/quote-avatar-p7.png';
import outcomeCollage from '../../../assets/images/nbnb/outcome-collage.png';
import {
  CaseStudyBulletList,
  CaseStudyContentCard,
  CaseStudyDoubleCardRow,
  CaseStudyHero,
  CaseStudyOverviewColumn,
  CaseStudyOverviewGrid,
  CaseStudySecondaryNav,
  CaseStudySectionHeader,
  CaseStudySplitSection,
  CaseStudyStatsBar,
} from '../../../components/cards/caseStudyCards/caseStudyCards';
import NbnbDesignSection from './nbnbDesignSection';
import ScrollToTopButton from '../../../components/scrollToTopButton/ScrollToTopButton';
import cards from '../../../components/cards/caseStudyCards/caseStudyCards.module.scss';
import page from './nbnb.module.scss';

const NBNB_INSTAGRAM_URL = 'https://www.instagram.com/nbnbgolfclub/';
const NGF_REPORT_URL = 'https://www.ngf.org/';

const NAV_ITEMS = [
  { id: 'section-intro', label: 'Intro' },
  { id: 'section-overview', label: 'Overview' },
  { id: 'section-problem', label: 'Problem Definition' },
  { id: 'section-research', label: 'Research' },
  { id: 'section-design', label: 'Visual Strategy' },
  { id: 'section-outcome', label: 'Outcome' },
  { id: 'section-takeaways', label: 'Takeaways' },
];

const NBNB_HERO_TAG_ROWS = [
  ['UI Design', 'Branding', 'Zero to Launch'],
  ['Social Media', 'Mobile App'],
];

const PROBLEM_COMPETITOR_ALT =
  'Direct competitive analysis spreadsheet comparing golf brand positioning';

const RESEARCH_THEMES_ALT =
  'Affinity map of user interview themes about barriers to accessing golf';

export default function NbnbCaseStudy() {
  const [activeSection, setActiveSection] = React.useState('section-intro');
  const { image, openLightbox, closeLightbox, closeButtonRef } = useImageLightbox();
  const scrollLockRef = React.useRef(null);
  const scrollLockTimerRef = React.useRef(null);
  const navWrapRef = React.useRef(null);

  ReactGA.pageview(window.location.pathname + window.location.search);

  React.useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  const updateActiveFromScroll = React.useCallback(() => {
    if (scrollLockRef.current) return;

    const scrollSpyOffset = (navWrapRef.current?.offsetHeight ?? 72) + 48;
    const scrollPos = window.scrollY + scrollSpyOffset;
    let current = NAV_ITEMS[0].id;

    for (const { id } of NAV_ITEMS) {
      const el = document.getElementById(id);
      if (!el) continue;
      const top = el.getBoundingClientRect().top + window.scrollY;
      if (top <= scrollPos) {
        current = id;
      }
    }

    setActiveSection(current);
  }, []);

  React.useEffect(() => {
    updateActiveFromScroll();

    const onScroll = () => {
      updateActiveFromScroll();
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', updateActiveFromScroll);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', updateActiveFromScroll);
      if (scrollLockTimerRef.current) {
        window.clearTimeout(scrollLockTimerRef.current);
      }
    };
  }, [updateActiveFromScroll]);

  const scrollToSection = (id) => {
    if (scrollLockTimerRef.current) {
      window.clearTimeout(scrollLockTimerRef.current);
    }

    scrollLockRef.current = id;
    setActiveSection(id);

    const el = document.getElementById(id);
    if (el) {
      const docTop = el.getBoundingClientRect().top + window.scrollY;
      const navH = navWrapRef.current?.offsetHeight ?? 72;
      const targetTop = id === 'section-intro' ? docTop : docTop - navH;
      window.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' });
    }

    scrollLockTimerRef.current = window.setTimeout(() => {
      scrollLockRef.current = null;
      scrollLockTimerRef.current = null;
      updateActiveFromScroll();
    }, 900);
  };

  return (
    <main className={page.page}>
      <div id="section-intro">
        <div className={`${page.inner} ${page.heroBand}`}>
          <CaseStudyHero
            className={page.nbnbHero}
            heroRowClassName={page.nbnbHeroRow}
            heroLeftClassName={page.nbnbHeroLeft}
            tagBarClassName={page.nbnbHeroTagBar}
            heroImageWrapClassName={page.nbnbHeroImageWrap}
            title="Building a Values-Led UI Strategy For a More Inclusive Future of Golf"
            tagRows={NBNB_HERO_TAG_ROWS}
            subhead="Visual Strategy & UI Design for Inclusive Sports Social Media Platforms"
            ctaHref={NBNB_INSTAGRAM_URL}
            ctaLabel="NBNB Instagram"
            heroImageSrc={nbnbHeroLanding}
            heroImageAlt="NBNB mobile and laptop mockups for the golf community product"
            heroImageAspectRatio="1757 / 783"
          />
        </div>

        <div className={`${page.inner} ${page.overviewBand}`}>
          <CaseStudyOverviewGrid>
            <CaseStudyOverviewColumn label="Timeline">
              <ul className={cards.overviewList}>
                <li>2 months</li>
              </ul>
            </CaseStudyOverviewColumn>
            <CaseStudyOverviewColumn label="My role">
              <ul className={cards.overviewList}>
                <li>Product Designer</li>
                <li>UX Researcher</li>
                <li>UX Designer</li>
                <li>Brand Designer</li>
              </ul>
            </CaseStudyOverviewColumn>
            <CaseStudyOverviewColumn label="Team">
              <ul className={cards.overviewList}>
                <li>1 Product Designer</li>
                <li>2 Founders</li>
              </ul>
            </CaseStudyOverviewColumn>
            <CaseStudyOverviewColumn label="Scope" wide>
              <ul className={cards.overviewList}>
                <li>
                  Conduct user and market research to craft and iterate on brand identity that reflects
                  foundational values.
                </li>
                <li>Partner with key stakeholders to shape and scope MVP.</li>
                <li>Social media app design for an inclusive, values-first golfing community.</li>
              </ul>
            </CaseStudyOverviewColumn>
          </CaseStudyOverviewGrid>
        </div>
      </div>

      <div ref={navWrapRef} className={page.navWrap}>
        <CaseStudySecondaryNav items={NAV_ITEMS} activeId={activeSection} onSelect={scrollToSection} />
      </div>

      <section className={`${page.section} ${page.sectionDarkPorcelain}`} id="section-overview">
        <div className={page.inner}>
          <CaseStudySplitSection
            kicker="OVERVIEW"
            title="Challenging Who the Golf Industry Feels Built For"
            imageSrc={overviewStats}
            imageAlt="Infographic showing growth in women golfers, non-Caucasian players, and beginner golfers"
            largeFooterGap
            splitFooter={
              <p className={cards.splitFooterQuestion}>
                This rapid diversification of the golf industry is forcing golf businesses and leaders to
                consider how to respond, support and harness the interest and buying power of the historically
                marginalized groups that are the growing face of the sport.
              </p>
            }
          >
            <div className={`${cards.splitBodyColumn} ${page.overviewSplitBody}`}>
              <p>
                No Bunkers No Bogeys was created to challenge the perception of golf as exclusive, inaccessible,
                and culturally narrow. Golf&apos;s popularity grew during the pandemic, with an 8% increase in
                non-Caucasian players, 11% increase in women players and an influx in over 3 million new golfers
                since 2022, according to the{' '}
                <a href={NGF_REPORT_URL} target="_blank" rel="noreferrer">
                  2022 NGF report
                </a>
                .
              </p>
              <p>
                As the sole end-to-end product designer, I led the brand strategy, visual identity, and
                foundational UI system for the platform. This project was not simply about designing a modern
                golf brand. It was about{' '}
                <strong>
                  translating values of inclusion, accessibility, and belonging into a cohesive visual language
                  within one of the most traditionally conservative industries in sports.
                </strong>
              </p>
            </div>
          </CaseStudySplitSection>
        </div>
      </section>

      <section className={`${page.section} ${page.sectionPorcelain}`} id="section-problem">
        <div className={page.inner}>
          <CaseStudySplitSection
            kicker="PROBLEM DEFINITION"
            title="The Problem Was Bigger Than Branding"
            splitMedia={
              <>
                <figure className={`${cards.splitFigure} ${cards.splitFigureTall}`}>
                  <button
                    type="button"
                    className={page.expandableImageButton}
                    onClick={(e) =>
                      openLightbox({ src: problemCompetitor, alt: PROBLEM_COMPETITOR_ALT }, e.currentTarget)
                    }
                    aria-label={`View full size: ${PROBLEM_COMPETITOR_ALT}`}
                  >
                    <img className={cards.splitImg} src={problemCompetitor} alt="" loading="lazy" />
                  </button>
                </figure>
                <div className={`${cards.mediaCaption} ${cards.mediaCaptionCenter}`}>
                  <p className={`${cards.mediaCaptionPlain} ${cards.mediaCaptionPlainCenter}`}>
                    Direct Competitor Analysis
                  </p>
                </div>
              </>
            }
          >
            <div className={`${cards.splitBodyColumn} ${page.problemSplitBody}`}>
              <p>
                The golf industry has historically leaned on masculine visual systems, dark green and blue
                palettes, and imagery centered around wealth, exclusivity, and tradition. Through conducting
                competitor research, I learned that many existing brands lacked representation across race,
                gender, age, ability, and economic background.
              </p>
              <p className={cards.splitBodyParagraphGap}>
                The founders wanted to build something fundamentally different:{' '}
                <strong>
                  a platform that felt welcoming, modern, community-driven, and culturally relevant without
                  losing credibility within the sport itself.
                </strong>
              </p>
              <p>
                The core challenge became balancing broad inclusivity with a clear and cohesive identity. The
                brand needed to feel elevated and aspirational while intentionally moving away from traditional
                golf aesthetics that reinforced exclusionary perceptions of the sport.
              </p>
            </div>
          </CaseStudySplitSection>
        </div>
      </section>

      <section className={`${page.section} ${page.sectionDarkPorcelain}`} id="section-research">
        <div className={page.inner}>
          <div className={cards.sectionBlock}>
            <CaseStudySectionHeader
              kicker="RESEARCH"
              title="Inclusion Starts With Understanding Who Feels Left Out"
            />
            <div className={page.researchStack}>
              <CaseStudyDoubleCardRow
                className={`${cards.doubleRowAlignStart} ${page.researchDoubleRow}`}
                left={
                  <div className={`${cards.splitBodyColumn} ${page.researchTextCol} ${page.researchThemesCopy}`}>
                    <p>
                      To ground the work in real user perspectives, I conducted 12 in-depth interviews and
                      additional surveys with golfers across a wide range of ages, races, genders, abilities, and
                      experience levels, from beginners to professionals.
                    </p>
                    <p>The research revealed recurring themes around underrepresented identities in golf:</p>
                    <ul>
                      <li>golf was perceived as intimidating and elitist</li>
                      <li>many users did not see themselves represented in golf culture</li>
                      <li>access to mentorship and community was limited</li>
                      <li>traditional branding often reinforced feelings of exclusion</li>
                    </ul>
                  </div>
                }
                right={
                  <div className={page.researchMediaCol}>
                    <button
                      type="button"
                      className={`${page.expandableImageButton} ${page.researchThemesImage}`}
                      onClick={(e) =>
                        openLightbox({ src: researchThemes, alt: RESEARCH_THEMES_ALT }, e.currentTarget)
                      }
                      aria-label={`View full size: ${RESEARCH_THEMES_ALT}`}
                    >
                      <img className={page.shadowMedia} src={researchThemes} alt="" loading="lazy" />
                    </button>
                    <div className={`${cards.mediaCaption} ${cards.mediaCaptionCenter}`}>
                      <p className={`${cards.mediaCaptionPlain} ${cards.mediaCaptionPlainCenter}`}>
                        Analysis of themes in User Interviews
                      </p>
                    </div>
                  </div>
                }
              />

              <CaseStudyDoubleCardRow
                className={`${page.researchDoubleRow} ${page.researchInsightRow}`}
                left={
                  <div className={page.researchMediaCol}>
                    <img
                      className={page.researchInsightImage}
                      src={researchInsight}
                      alt="Illustration of a golfer with statistics on representation and course experiences"
                      loading="lazy"
                    />
                  </div>
                }
                right={
                  <CaseStudyContentCard
                    surface="none"
                    className={`${cards.contentCardPaddingNone} ${page.researchTextCol}`}
                  >
                    <div className={`${cards.splitBodyColumn} ${page.researchInsightCopy}`}>
                      <p>One key insight shaped the direction of the project:</p>
                      <p>
                        <strong>
                          Designing for &ldquo;everyone&rdquo; is impossible. The goal is designing authentically
                          while creating systems that feel welcoming and accessible to a broad range of people.
                        </strong>
                      </p>
                      <p>That philosophy became the foundation of the brand strategy.</p>
                    </div>
                  </CaseStudyContentCard>
                }
              />

              <div className={page.participantQuoteGrid}>
                <figure className={page.participantQuoteCol}>
                  <img
                    className={page.participantQuoteAvatar}
                    src={quoteAvatarP3}
                    alt=""
                    loading="lazy"
                  />
                  <blockquote className={page.participantQuoteText}>
                    &ldquo;I definitely go [to the course] and I&apos;m like, man, you know, I wish there were
                    more minorities here.&rdquo;
                  </blockquote>
                  <figcaption>
                    <p className={page.participantQuoteAttr}>-Participant 3</p>
                    <p className={page.participantQuoteRole}>Advanced Golfer</p>
                  </figcaption>
                </figure>
                <div className={page.participantQuoteDivider} aria-hidden />
                <figure className={page.participantQuoteCol}>
                  <img
                    className={page.participantQuoteAvatar}
                    src={quoteAvatarP2}
                    alt=""
                    loading="lazy"
                  />
                  <blockquote className={page.participantQuoteText}>
                    &ldquo;A lot of females won&apos;t join certain clubs...why would we pay the same fees if we
                    don&apos;t have the same access.&rdquo;
                  </blockquote>
                  <figcaption>
                    <p className={page.participantQuoteAttr}>-Participant 2</p>
                    <p className={page.participantQuoteRole}>Advanced Golfer</p>
                  </figcaption>
                </figure>
                <div className={page.participantQuoteDivider} aria-hidden />
                <figure className={page.participantQuoteCol}>
                  <img
                    className={page.participantQuoteAvatar}
                    src={quoteAvatarP7}
                    alt=""
                    loading="lazy"
                  />
                  <blockquote className={page.participantQuoteText}>
                    &ldquo;Growing up I was the only black girl playing...I was in the Big Sky Conference, and I
                    only saw 1 or 2 other black girls.&rdquo;
                  </blockquote>
                  <figcaption>
                    <p className={page.participantQuoteAttr}>-Participant 7</p>
                    <p className={page.participantQuoteRole}>Professional Golfer</p>
                  </figcaption>
                </figure>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className={`${page.section} ${page.sectionPorcelain}`} id="section-design">
        <div className={page.inner}>
          <NbnbDesignSection />
        </div>
      </section>

      <section className={`${page.section} ${page.sectionDarkPorcelain}`} id="section-outcome">
        <div className={page.inner}>
          <div className={cards.sectionBlock}>
            <CaseStudySectionHeader kicker="OUTCOME" title="Designing Belonging Into the Experience" />
            <div className={page.outcomeRow}>
              <div className={`${cards.splitBodyColumn} ${page.outcomeCopy}`}>
                <p>
                  The brand launched publicly and was well received by both stakeholders and users. The new
                  identity helped differentiate No Bunkers No Bogeys within a highly traditional industry while
                  creating stronger alignment between the organization&apos;s mission and visual presence.
                </p>
                <p>
                  The project also contributed to increased founder confidence, growth across social channels,
                  and early platform engagement.
                </p>
              </div>
              <div className={page.outcomeStatsWrap}>
                <CaseStudyStatsBar
                  animateOnScroll
                  className={page.statsBarOutcomeSingleRow}
                  donutVariant="cdOutcome"
                  stats={[
                    { value: '400%', label: 'social growth', progress: 1 },
                    { value: '1k', label: 'new members', progress: 1 },
                  ]}
                />
              </div>
            </div>
            <p className={page.proseCallout}>
              The success demonstrated how thoughtful visual strategy can help reshape who feels welcomed into
              historically exclusionary spaces.
            </p>
            <figure className={page.outcomeCollage}>
              <img
                src={outcomeCollage}
                alt="Collage of NBNB brand launch materials including mobile app, website, and marketing assets"
                loading="lazy"
              />
            </figure>
          </div>
        </div>
      </section>

      <section className={page.section} id="section-takeaways">
        <div className={page.inner}>
          <div className={page.takeawaysBlock}>
            <CaseStudySectionHeader
              kicker="TAKEAWAYS"
              title="Branding Is Communication, Not Decoration"
              titleClassName={cards.sectionTitleLarge}
              asideImg={takeawaysEmoji}
              asideAlt="Decorative avatar"
              asideImgClassName={cards.sectionAsideImgSm}
            />
            <CaseStudyDoubleCardRow
              className={cards.doubleRowAlignStart}
              left={
                <CaseStudyContentCard
                  label="What I learned"
                  surface="none"
                  labelTone="teal"
                  className={cards.contentCardPaddingNone}
                >
                  <CaseStudyBulletList
                    items={[
                      'Inclusive design is not about trying to represent everyone equally. It is about building systems that reduce barriers and create space for broader participation.',
                      'Visual language shapes emotional perception long before users interact with a product. Visual design can either reinforce exclusion or invite curiosity and belonging.',
                      'Accessibility and brand strategy are deeply connected. Readability, interaction patterns, color contrast, and representation all influence whether users feel welcomed.',
                      'Strong visual systems require iteration and restraint. Some of the most impactful decisions came from refining and simplifying early concepts rather than adding more.',
                    ]}
                  />
                </CaseStudyContentCard>
              }
              right={
                <CaseStudyContentCard
                  label="What this work demonstrates"
                  surface="none"
                  labelTone="teal"
                  className={cards.contentCardPaddingNone}
                >
                  <CaseStudyBulletList
                    items={[
                      'I approach visual / UI design as a strategic systems problem, not just a visual exercise. My work connects organizational values, user psychology, accessibility, and product experience.',
                      'I can lead ambiguous, early-stage initiatives end-to-end: from research and strategy through execution and scalable system design.',
                      'I am comfortable navigating stakeholder discussions, balancing bold ideas with practical constraints, and using research to guide decision-making.',
                      'I design with a human-centered and values-led mindset, creating experiences that prioritize emotional resonance, accessibility, and long-term scalability.',
                    ]}
                  />
                </CaseStudyContentCard>
              }
            />
          </div>
        </div>
      </section>

      <ImageLightbox
        image={image}
        onClose={closeLightbox}
        closeButtonRef={closeButtonRef}
      />
      <ScrollToTopButton />
    </main>
  );
}
